import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faHome } from "@fortawesome/free-solid-svg-icons";
import { Breadcrumb, Button, Card, Col, Form, Row, Table } from "@themesberg/react-bootstrap";
import { Link, useParams, generatePath } from "react-router-dom";

import { Routes } from "../routes";
import defaultGroups from "../data/groups";
import { normalizePhone } from "../utils/mpesa";
import { enqueueSync } from "../utils/syncQueue";
import { apiRequest, getAuthToken } from "../utils/api";
import { formatPersonName } from "../utils/personName";

const GROUPS_STORAGE_KEY = "treasurer.groups";

const isValidPhone = (value = "") => {
  if (!value) {
    return true;
  }
  const normalized = normalizePhone(value);
  return /^\+?\d{9,15}$/.test(normalized);
};

const loadGroups = () => {
  const stored = localStorage.getItem(GROUPS_STORAGE_KEY);
  if (!stored) {
    return defaultGroups;
  }
  try {
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) {
      return defaultGroups;
    }
    return parsed.map((group) => ({
      ...group,
      frequency: group.frequency || "monthly",
      members: Array.isArray(group.members)
        ? group.members
            .map((member) => {
              if (typeof member === "string") {
                return { id: null, name: member, phone: "", info: "" };
              }
              return {
                id: member?.id ?? null,
                name: member?.name || "",
                phone: member?.phone || "",
                info: member?.info || ""
              };
            })
            .filter((member) => member.name)
        : []
    }));
  } catch (error) {
    return defaultGroups;
  }
};

export default () => {
  const { groupId } = useParams();
  const [groups, setGroups] = useState(loadGroups);
  const [memberInput, setMemberInput] = useState({ name: "", phone: "", info: "" });
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [editingMember, setEditingMember] = useState({ name: "", phone: "", info: "" });

  useEffect(() => {
    localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify(groups));
  }, [groups]);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      return;
    }
    let isMounted = true;
    const loadRemote = async () => {
      try {
        const [groupData, membersData] = await Promise.all([
          apiRequest(`/groups/${groupId}`),
          apiRequest(`/groups/${groupId}/members`)
        ]);
        if (!isMounted) {
          return;
        }
        if (groupData?.group) {
          setGroups((prev) => {
            const next = prev.map((item) =>
              String(item.id) === String(groupId)
                ? {
                    ...item,
                    name: groupData.group.name,
                    purpose: groupData.group.purpose,
                    frequency: groupData.group.frequency || "monthly",
                    createdAt: groupData.group.createdAt || item.createdAt
                  }
                : item
            );
            if (next.some((item) => String(item.id) === String(groupId))) {
              return next;
            }
            return [
              ...next,
              {
                id: String(groupData.group.id),
                name: groupData.group.name,
                purpose: groupData.group.purpose,
                frequency: groupData.group.frequency || "monthly",
                createdAt: groupData.group.createdAt || new Date().toISOString(),
                members: []
              }
            ];
          });
        }
        if (membersData?.members) {
          setGroups((prev) =>
            prev.map((item) => {
              if (String(item.id) !== String(groupId)) {
                return item;
              }
              return {
                ...item,
                members: membersData.members.map((member) => ({
                  id: member.id,
                  name: member.name,
                  phone: member.phone || "",
                  info: member.info || ""
                }))
              };
            })
          );
        }
      } catch (error) {
        // keep local data
      }
    };
    loadRemote();
    return () => {
      isMounted = false;
    };
  }, [groupId]);

  const group = useMemo(
    () => groups.find((item) => String(item.id) === String(groupId)),
    [groups, groupId]
  );

  const groupMembers = useMemo(() => {
    return Array.isArray(group?.members) ? group.members : [];
  }, [group]);

  const canSaveMember =
    memberInput.name.trim().length > 0 && isValidPhone(memberInput.phone);
  const canSaveMemberEdit =
    editingMember.name.trim().length > 0 && isValidPhone(editingMember.phone);

  const handleAddMember = () => {
    if (!group || !canSaveMember) {
      return;
    }
    const tempMemberId = `local-${Date.now()}`;
    const nextMember = {
      id: tempMemberId,
      name: memberInput.name.trim(),
      phone: memberInput.phone.trim(),
      info: memberInput.info.trim()
    };
    enqueueSync({
      type: "group.member.add",
      payload: { groupId: String(groupId), groupName: group?.name || "", member: nextMember }
    });
    const token = getAuthToken();
    if (token && navigator.onLine) {
      apiRequest(`/groups/${groupId}/members`, {
        method: "POST",
        body: JSON.stringify({
          name: nextMember.name,
          phone: nextMember.phone,
          info: nextMember.info
        })
      })
        .then((data) => {
          if (data?.member?.id) {
            setGroups((prev) =>
              prev.map((item) => {
                if (String(item.id) !== String(groupId)) {
                  return item;
                }
                const existing = Array.isArray(item.members) ? item.members : [];
                return {
                  ...item,
                  members: existing.map((member) =>
                    member.id === tempMemberId ? { ...member, id: data.member.id } : member
                  )
                };
              })
            );
          }
        })
        .catch(() => {});
    }
    setGroups((prev) =>
      prev.map((item) => {
        if (String(item.id) !== String(groupId)) {
          return item;
        }
        const existing = Array.isArray(item.members) ? item.members : [];
        const alreadyExists = existing.some(
          (member) => member.name.toLowerCase() === nextMember.name.toLowerCase()
        );
        return {
          ...item,
          members: alreadyExists ? existing : [...existing, nextMember]
        };
      })
    );
    setMemberInput({ name: "", phone: "", info: "" });
  };

  const handleStartEditMember = (member, index) => {
    setEditingMemberId(index);
    setEditingMember({
      name: member.name || "",
      phone: member.phone || "",
      info: member.info || ""
    });
  };

  const handleCancelEditMember = () => {
    setEditingMemberId(null);
    setEditingMember({ name: "", phone: "", info: "" });
  };

  const handleSaveEditMember = (member, index) => {
    if (!group || !canSaveMemberEdit) {
      return;
    }
    const updatedMember = {
      name: editingMember.name.trim(),
      phone: editingMember.phone.trim(),
      info: editingMember.info.trim()
    };
    enqueueSync({
      type: "group.member.update",
      payload: { groupId: String(groupId), member, updatedMember }
    });
    if (member.id && navigator.onLine) {
      apiRequest(`/members/${member.id}`, {
        method: "PUT",
        body: JSON.stringify(updatedMember)
      }).catch(() => {});
    }
    setGroups((prev) =>
      prev.map((item) => {
        if (String(item.id) !== String(groupId)) {
          return item;
        }
        const existing = Array.isArray(item.members) ? item.members : [];
        const nextMembers = existing.map((current, idx) => {
          if (member.id && current.id === member.id) {
            return { ...updatedMember, id: current.id };
          }
          if (!member.id && idx === index) {
            return { ...updatedMember, id: current.id };
          }
          return current;
        });
        return { ...item, members: nextMembers };
      })
    );
    handleCancelEditMember();
  };

  const ledgerPath = generatePath(Routes.GroupTransactions.path, { groupId });

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <Breadcrumb className="d-none d-md-inline-block" listProps={{ className: "breadcrumb-dark breadcrumb-transparent" }}>
            <Breadcrumb.Item>
              <FontAwesomeIcon icon={faHome} />
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={Routes.Groups.path}>Groups</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={ledgerPath}>{group?.name || "Group"}</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item active>Members</Breadcrumb.Item>
          </Breadcrumb>
          <h4>Members</h4>
          <p className="mb-0">View and manage members for this group.</p>
        </div>
        <Button
          as={Link}
          to={ledgerPath}
          variant="outline-primary"
          size="sm"
          className="d-inline-flex align-items-center"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to group
        </Button>
      </div>

      <Row>
        <Col xs={12} lg={10}>
          <Card border="light" className="shadow-sm mb-4">
            <Card.Header className="d-flex align-items-center justify-content-between">
              <h5 className="mb-0">Members</h5>
              <span className="text-muted">
                {groupMembers.length} member{groupMembers.length !== 1 ? "s" : ""}
              </span>
            </Card.Header>
            <Card.Body>
              {groupMembers.length === 0 ? (
                <p className="text-muted mb-3">No members added yet.</p>
              ) : (
                <Table responsive className="table-centered align-items-center mb-3">
                  <thead className="thead-light">
                    <tr>
                      <th className="border-0">Name</th>
                      <th className="border-0">Phone</th>
                      <th className="border-0">Info</th>
                      <th className="border-0 text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupMembers.map((member, index) => {
                      const isEditing = editingMemberId === index;
                      return (
                        <tr key={member.id ? `${member.id}` : `${member.name}-${member.phone}-${member.info}-${index}`}>
                          <td className="fw-bold">
                            {isEditing ? (
                              <Form.Control
                                size="sm"
                                value={editingMember.name}
                                onChange={(event) => {
                                  const { value } = event.target;
                                  setEditingMember((prev) => ({ ...prev, name: value }));
                                }}
                              />
                            ) : (
                              formatPersonName(member.name) || member.name
                            )}
                          </td>
                          <td>
                            {isEditing ? (
                              <Form.Control
                                size="sm"
                                value={editingMember.phone}
                                onChange={(event) => {
                                  const { value } = event.target;
                                  setEditingMember((prev) => ({ ...prev, phone: value }));
                                }}
                              />
                            ) : (
                              member.phone || "--"
                            )}
                            {isEditing && !isValidPhone(editingMember.phone) ? (
                              <small className="text-danger d-block">Invalid phone</small>
                            ) : null}
                          </td>
                          <td>
                            {isEditing ? (
                              <Form.Control
                                size="sm"
                                value={editingMember.info}
                                onChange={(event) => {
                                  const { value } = event.target;
                                  setEditingMember((prev) => ({ ...prev, info: value }));
                                }}
                              />
                            ) : (
                              member.info || "--"
                            )}
                          </td>
                          <td className="text-end">
                            {isEditing ? (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  className="me-2"
                                  onClick={() => handleSaveEditMember(member, index)}
                                  disabled={!canSaveMemberEdit}
                                >
                                  Save
                                </Button>
                                <Button size="sm" variant="outline-secondary" onClick={handleCancelEditMember}>
                                  Cancel
                                </Button>
                              </>
                            ) : (
                              <Button size="sm" variant="outline-secondary" onClick={() => handleStartEditMember(member, index)}>
                                Edit
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
              <Form
                onSubmit={(event) => {
                  event.preventDefault();
                  handleAddMember();
                }}
              >
                <Row className="g-2">
                  <Col md={4}>
                    <Form.Control
                      type="text"
                      placeholder="Name"
                      value={memberInput.name}
                      onChange={(event) => {
                        const { value } = event.target;
                        setMemberInput((prev) => ({ ...prev, name: value }));
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          handleAddMember();
                        }
                      }}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Control
                      type="text"
                      placeholder="Phone"
                      value={memberInput.phone}
                      onChange={(event) => {
                        const { value } = event.target;
                        setMemberInput((prev) => ({ ...prev, phone: value }));
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          handleAddMember();
                        }
                      }}
                    />
                    {!isValidPhone(memberInput.phone) ? (
                      <small className="text-danger d-block">Enter a valid phone number.</small>
                    ) : null}
                  </Col>
                  <Col md={4}>
                    <Form.Control
                      type="text"
                      placeholder="Info (optional)"
                      value={memberInput.info}
                      onChange={(event) => {
                        const { value } = event.target;
                        setMemberInput((prev) => ({ ...prev, info: value }));
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          handleAddMember();
                        }
                      }}
                    />
                  </Col>
                </Row>
                <Button
                  className="mt-3"
                  variant="outline-primary"
                  type="button"
                  onClick={handleAddMember}
                  disabled={!canSaveMember}
                >
                  Add member
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};
